import { useCallback, useRef, useState } from "react"
import {Trash2, Wheat, Plus, AlertCircle} from 'lucide-react'
import { type IngredientCategory, useCalculator } from "@/context/CalculatorContext"

const CATEGORY_OPTIONS: { label: string; value: IngredientCategory }[] = [
  { label: "Water", value: "water" },
  { label: "Salt", value: "salt" },
  { label: "Yeast", value: "yeast" },
  { label: "Sourdough Starter", value: "starter" },
  { label: "Sugar", value: "sugar" },
  { label: "Butter", value: "butter" },
  { label: "Custom", value: "custom" },
]

const CATEGORY_COLORS: Record<IngredientCategory, string> = {
  flour: "bg-indigo-500",
  water: "bg-sky-500",
  salt: "bg-slate-400",
  yeast: "bg-amber-500",
  starter: "bg-orange-600",
  sugar: "bg-pink-500",
  butter: "bg-yellow-500",
  custom: "bg-gray-400",
}

interface FieldErrors {
  weight?: string
  percentage?: string
}

// Parses a raw input string and returns a safe numeric value plus an optional
// validation message. Negative values and non-numeric input never reach the
// calculator state as-is; they are clamped to 0 while surfacing a clear error.
function validateNumericInput(raw: string): { value: number; error: string | null } {
  const trimmed = raw.trim()
  if (trimmed === "") return { value: 0, error: null }

  const parsed = parseFloat(trimmed)

  if (isNaN(parsed)) {
    return { value: 0, error: "Enter a valid number" }
  }
  if (!isFinite(parsed)) {
    return { value: 0, error: "Value is too large" }
  }
  if (parsed < 0) {
    return { value: 0, error: "Cannot be negative" }
  }
  return { value: parsed, error: null }
}

export default function IngredientSpreadsheet() {
  const {
    ingredients,
    updateIngredientWeight,
    updateIngredientPercentage,
    updateIngredientName,
    addIngredient,
    removeIngredient,
    totalDoughWeight,
    totalTrueHydration,
  } = useCalculator()

  const [showDropdown, setShowDropdown] = useState(false)
  const [customName, setCustomName] = useState("")
  const [addingCustom, setAddingCustom] = useState(false)
  const [errors, setErrors] = useState<Record<string, FieldErrors>>({})
  const dropdownRef = useRef<HTMLDivElement>(null)

  const handleAddCategory = useCallback(
    (category: IngredientCategory) => {
      if (category === "custom") {
        setAddingCustom(true)
        setShowDropdown(false)
        return
      }
      addIngredient(category)
      setShowDropdown(false)
    },
    [addIngredient]
  )

  const handleAddCustom = useCallback(() => {
    if (customName.trim()) {
      addIngredient("custom", customName.trim())
      setCustomName("")
      setAddingCustom(false)
    }
  }, [addIngredient, customName])

  const handleRemove = useCallback((id: string) => {
    removeIngredient(id)
    setErrors((prev) => {
      const next = { ...prev }
      delete next[id]
      return next
    })
  }, [removeIngredient])

  const handleWeightChange = useCallback(
    (id: string, raw: string) => {
      const { value, error } = validateNumericInput(raw)
      setErrors((prev) => ({ ...prev, [id]: { ...prev[id], weight: error || undefined } }))
      updateIngredientWeight(id, value)
    },
    [updateIngredientWeight]
  )

  const handlePercentageChange = useCallback(
    (id: string, raw: string) => {
      const { value, error } = validateNumericInput(raw)
      setErrors((prev) => ({ ...prev, [id]: { ...prev[id], percentage: error || undefined } }))
      updateIngredientPercentage(id, value)
    },
    [updateIngredientPercentage]
  )

  const formatNum = (n: number, decimals = 1) => {
    if (!isFinite(n) || isNaN(n)) return "0"
    return n.toFixed(decimals)
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 overflow-hidden print-formula shadow-sm">
      <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-white to-indigo-50/30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 bg-indigo-50 rounded-lg flex items-center justify-center flex-shrink-0">
            <Wheat size={17} className="text-indigo-600" strokeWidth={2.2} />
          </div>
          <div>
            <h2 className="text-base font-semibold text-gray-900">Ingredient Formula</h2>
            <p className="text-xs text-gray-500 mt-0.5">Edit weights or percentages — changes sync instantly</p>
          </div>
        </div>
        <span className="hidden sm:inline-flex text-xs font-semibold text-gray-500 bg-gray-100 px-2.5 py-1 rounded-full tabular-nums flex-shrink-0">
          {ingredients.length} {ingredients.length === 1 ? "item" : "items"}
        </span>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <div className="max-h-[440px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 z-10">
              <tr className="bg-gray-50 border-b border-gray-100">
                <th className="text-left text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-auto">
                  Ingredient
                </th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-32">
                  Weight (g)
                </th>
                <th className="text-right text-xs font-semibold text-gray-500 uppercase tracking-wider px-4 py-3 w-28">
                  Baker's %
                </th>
                <th className="w-10 px-2 py-3 no-print"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {ingredients.map((ing) => {
                const weightError = errors[ing.id]?.weight
                const percentageError = errors[ing.id]?.percentage
                return (
                  <tr
                    key={ing.id}
                    className={`group transition-colors ${
                      ing.locked ? "bg-indigo-50/40" : "hover:bg-gray-50/60"
                    }`}
                  >
                    {/* Name */}
                    <td className="px-4 py-2.5 align-top">
                      {ing.locked ? (
                        <div className="flex items-center gap-2 h-[30px]">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${CATEGORY_COLORS[ing.category]}`}></div>
                          <span className="font-semibold text-indigo-700 text-sm">{ing.name}</span>
                          <span className="text-xs text-indigo-400 font-medium">(Basis)</span>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 h-[30px]">
                          <div className={`w-2 h-2 rounded-full flex-shrink-0 ${CATEGORY_COLORS[ing.category]}`}></div>
                          <input
                            type="text"
                            value={ing.name}
                            onChange={(e) => updateIngredientName(ing.id, e.target.value)}
                            className="w-full bg-transparent text-gray-800 text-sm focus:outline-none focus:bg-white border border-transparent focus:border-gray-200 rounded px-1.5 py-0.5 transition-colors"
                          />
                        </div>
                      )}
                    </td>

                    {/* Weight */}
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex items-center justify-end gap-1 h-[30px]">
                        {weightError && (
                          <AlertCircle size={13} className="text-red-500 flex-shrink-0" aria-hidden="true" />
                        )}
                        <input
                          type="number"
                          min="0"
                          step="1"
                          value={ing.weight === 0 ? "" : formatNum(ing.weight, ing.weight % 1 === 0 ? 0 : 1)}
                          onChange={(e) => handleWeightChange(ing.id, e.target.value)}
                          aria-invalid={!!weightError}
                          title={weightError || undefined}
                          className={`w-24 text-right tabular-nums rounded-md px-2 py-1 text-sm border transition-colors focus:outline-none focus:ring-2 ${
                            weightError
                              ? "bg-red-50 border-red-300 text-red-700 focus:ring-red-300"
                              : ing.locked
                              ? "bg-indigo-50 border-indigo-200 text-indigo-800 font-semibold focus:ring-indigo-300"
                              : "bg-white border-gray-200 text-gray-800 hover:border-gray-300 focus:ring-indigo-300"
                          }`}
                        />
                        <span className="text-xs text-gray-400 w-4">g</span>
                      </div>
                      {weightError && (
                        <p className="text-[10px] font-medium text-red-500 text-right mt-1 leading-tight">
                          {weightError}
                        </p>
                      )}
                    </td>

                    {/* Percentage */}
                    <td className="px-4 py-2.5 align-top">
                      <div className="flex items-center justify-end gap-1 h-[30px]">
                        {ing.locked ? (
                          <span className="w-20 text-right tabular-nums text-sm font-bold text-indigo-700 bg-indigo-50 rounded-md px-2 py-1 border border-indigo-200">
                            100.0
                          </span>
                        ) : (
                          <>
                            {percentageError && (
                              <AlertCircle size={13} className="text-red-500 flex-shrink-0" aria-hidden="true" />
                            )}
                            <input
                              type="number"
                              min="0"
                              step="0.1"
                              value={ing.percentage === 0 ? "" : formatNum(ing.percentage, 1)}
                              onChange={(e) => handlePercentageChange(ing.id, e.target.value)}
                              aria-invalid={!!percentageError}
                              title={percentageError || undefined}
                              className={`w-20 text-right tabular-nums rounded-md px-2 py-1 text-sm border transition-colors focus:outline-none focus:ring-2 ${
                                percentageError
                                  ? "bg-red-50 border-red-300 text-red-700 focus:ring-red-300"
                                  : "border-gray-200 bg-white text-gray-800 hover:border-gray-300 focus:ring-indigo-300"
                              }`}
                            />
                          </>
                        )}
                        <span className="text-xs text-gray-400 w-4">%</span>
                      </div>
                      {percentageError && (
                        <p className="text-[10px] font-medium text-red-500 text-right mt-1 leading-tight">
                          {percentageError}
                        </p>
                      )}
                    </td>

                    {/* Delete */}
                    <td className="px-2 py-2.5 align-top no-print">
                      <div className="h-[30px] flex items-center">
                        {!ing.locked && (
                          <button
                            onClick={() => handleRemove(ing.id)}
                            className="opacity-0 group-hover:opacity-100 p-1.5 rounded-md text-gray-300 hover:text-red-500 hover:bg-red-50 transition-all"
                            title="Remove ingredient"
                          >
                            <Trash2 size={14} />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add Ingredient */}
      <div className="px-4 py-3 border-t border-gray-100 no-print">
        {addingCustom ? (
          <div className="flex items-center gap-2">
            <input
              autoFocus
              type="text"
              value={customName}
              onChange={(e) => setCustomName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") handleAddCustom()
                if (e.key === "Escape") setAddingCustom(false)
              }}
              placeholder="Ingredient name..."
              className="flex-1 text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-300 focus:border-transparent"
            />
            <button
              onClick={handleAddCustom}
              className="text-sm font-medium bg-indigo-600 text-white px-3 py-2 rounded-lg hover:bg-indigo-700 transition-colors"
            >
              Add
            </button>
            <button
              onClick={() => setAddingCustom(false)}
              className="text-sm text-gray-500 px-3 py-2 rounded-lg hover:bg-gray-100 transition-colors"
            >
              Cancel
            </button>
          </div>
        ) : (
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={() => setShowDropdown((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-semibold text-indigo-600 hover:text-indigo-700 bg-indigo-50 hover:bg-indigo-100 px-3 py-2 rounded-lg transition-colors"
            >
              <Plus size={15} strokeWidth={2.5} />
              Add Ingredient
            </button>

            {showDropdown && (
              <div className="absolute top-full left-0 mt-1.5 bg-white rounded-xl shadow-lg border border-gray-100 py-1 z-10 min-w-48">
                {CATEGORY_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => handleAddCategory(opt.value)}
                    className="w-full flex items-center gap-2.5 text-left text-sm text-gray-700 hover:bg-indigo-50 hover:text-indigo-700 px-4 py-2.5 transition-colors"
                  >
                    <span className={`w-2 h-2 rounded-full flex-shrink-0 ${CATEGORY_COLORS[opt.value]}`}></span>
                    {opt.label}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Summary Footer */}
      <div className="bg-gray-50 border-t border-gray-200 px-5 py-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-white rounded-lg border border-gray-200 px-4 py-3">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">
              Total Dough Weight
            </p>
            <p className="text-2xl font-bold text-gray-900 tabular-nums">
              {formatNum(totalDoughWeight, 0)}
              <span className="text-sm font-medium text-gray-400 ml-1">g</span>
            </p>
          </div>
          <div className="bg-gradient-to-br from-indigo-50 to-white rounded-lg border border-indigo-200 px-4 py-3">
            <p className="text-xs font-semibold text-indigo-500 uppercase tracking-wider mb-1">
              True Hydration
            </p>
            <p className="text-2xl font-bold text-indigo-700 tabular-nums">
              {formatNum(totalTrueHydration, 1)}
              <span className="text-sm font-medium text-indigo-400 ml-1">%</span>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}
